import { usePatientQuery } from '@ctw/shared/context/patient-provider';
import { getAiServiceBaseUrl } from '@ctw/shared/utils/environments';
import { QUERY_KEY_AI_EXTRACTION_JOBS } from '@ctw/shared/utils/query-keys';

export interface ExtractionFieldValue {
  display: string;
  value: string;
  citationIds: Array<number>;
}

export interface ExtractionGroup {
  display: string;
  values: Record<string, ExtractionFieldValue>;
}

export interface ExtractionCitation {
  id: number;
  resourceType: string;
  resourceId: string;
}

export interface ExtractionJobAttributes {
  status: string;
  createdAt: string;
  updatedAt: string;
  groups: Record<string, ExtractionGroup>;
  citations: Array<ExtractionCitation>;
}

export interface ExtractionJob {
  type: string;
  id: string;
  attributes: ExtractionJobAttributes;
  relationships?: {
    patient?: {
      data?: { type: string; id: string };
    };
  };
}

// JSON:API response from the summarization service
interface ExtractionJobsResponse {
  data: Array<ExtractionJob>;
}

export function useExtractionJobs(upid: string | undefined) {
  return usePatientQuery<Array<ExtractionJob>>({
    queryId: QUERY_KEY_AI_EXTRACTION_JOBS,
    queryKey: [upid],
    queryFn: async ({ requestContext, ctwFetch }) => {
      if (!upid) {
        return [];
      }

      const endpoint = `${getAiServiceBaseUrl({
        env: requestContext.env,
      })}/extraction-jobs?upid=${encodeURIComponent(upid)}`;

      const response = await ctwFetch<ExtractionJobsResponse>(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${requestContext.authToken}`,
          'Zus-Account': requestContext.builderId,
        },
      });

      return response.data.data ?? [];
    },
  });
}
